import type {
  CustomPropertyValidator,
  CustomValidationRules,
  ValidationSchema,
} from '@wix/editor-elements-corvid-utils';
import {
  reportError,
  reportWarning,
  messages,
  assert,
} from '@wix/editor-elements-corvid-utils';

import type {
  BreadcrumbsMapperProps,
  BreadcrumbsSDKBase,
  Breadcrumb,
  BreadcrumbMetaData,
} from '../Breadcrumbs.types';

const hasRequiredProperties: CustomPropertyValidator<BreadcrumbsMapperProps> = (
  breadcrumbs: Array<Breadcrumb>,
) => {
  return breadcrumbs.every(({ label, icon }, index) => {
    if (!label && !icon) {
      reportError(
        messages.invalidOptionFields({
          propertyName: 'Breadcrumbs.Item',
          index,
          wrongValue: breadcrumbs[index],
          fields: ['icon', 'label'],
        }),
      );

      return false;
    }

    return true;
  });
};

const isValidIcon: CustomPropertyValidator<BreadcrumbsMapperProps> = (
  breadcrumbs: Array<Breadcrumb>,
) => {
  return breadcrumbs.every(({ icon }) => {
    if (icon && !assert.isSVG(icon)) {
      reportError(messages.invalidSvgValue(icon));
      return false;
    }

    return true;
  });
};

const isValidLink: CustomPropertyValidator<BreadcrumbsMapperProps> = (
  breadcrumbs: Array<Breadcrumb>,
  api,
) => {
  const { linkUtils } = api.platformUtils;

  return breadcrumbs.every(({ link }, index) => {
    if (assert.isNil(link)) {
      return true;
    }

    try {
      /**
       * User can enter anything into `link` property.
       *
       * So we use `linkUtils.getLinkProps`, which during
       * converting into `LinkProps` will validate link
       * and can throw UnsupportedLinkType error
       */
      const { type } = linkUtils.getLinkProps(link, '_blank');
      if (type === 'ExternalLink') {
        reportError(
          messages.invalidItemLink({
            propertyName: 'link',
            functionName: 'Breadcrumbs.Item',
            index,
          }),
        );

        return false;
      }
    } catch (e) {
      // Making UnsupportedLinkType user friendly
      reportError(
        messages.unsupportedLinkType({
          propertyName: 'link',
          functionName: 'Breadcrumbs.Items',
          wrongValue: link,
          index,
        }),
      );

      return false;
    }

    return true;
  });
};

export const validateLimits = (
  value: number,
  breadcrumbs: Array<BreadcrumbMetaData>,
  functionName: string,
  propertyName: string,
): boolean => {
  if (value > 0 && value < breadcrumbs.length) {
    return true;
  }
  if (value < 1) {
    reportError(
      messages.invalidNumberBoundsMessage({
        value,
        minimum: 1,
        functionName,
        propertyName,
        index: undefined,
      }),
    );
    return false;
  } else {
    reportWarning(
      messages.invalidNumberBoundsMessage({
        value,
        minimum: 1,
        maximum: breadcrumbs.length - 1,
        functionName,
        propertyName,
        index: undefined,
      }),
    );
    return true;
  }
};

export const customValidators: CustomValidationRules<
  BreadcrumbsMapperProps,
  BreadcrumbsSDKBase
> = {
  items: [hasRequiredProperties, isValidLink, isValidIcon],
};

export const validationSchema: ValidationSchema = {
  type: ['object'],
  properties: {
    itemsBeforeEllipsis: {
      type: ['number'],
    },
    itemsAfterEllipsis: {
      type: ['number'],
    },
    items: {
      type: ['array'],
      items: {
        type: ['object'],
        properties: {
          label: {
            type: ['string', 'nil'],
          },
          icon: {
            type: ['string', 'nil'],
          },
          link: {
            type: ['string', 'nil'],
          },
        },
      },
    },
  },
};
